import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Send, ShoppingBag, Sparkles, Truck } from "lucide-react";
import SoccerBall from "@/components/SoccerBall";
import CollectionArt from "@/components/CollectionArt";
import SEO from "@/components/SEO";
import ShopifyBuyButton from "@/components/ShopifyBuyButton";
import FeaturedProductGrid from "@/components/shop/FeaturedProductGrid";
import ShopCategoryLauncher from "@/components/shop/ShopCategoryLauncher";
import TeamSetSection from "@/components/shop/TeamSetSection";
import GoldSingleCard from "@/components/shop/GoldSingleCard";
import { fetchFeaturedTeamSets, fetchGoldSinglesPremium } from "@/lib/shopifyStorefront";

/**
 * /shop — Storefront powered by Shopify.
 *
 * Section order (single long page):
 *   1. Hero
 *   2. Sticky Jump Nav
 *   3. Featured Collectibles (Shopify Buy Button embed)
 *   4. Gold Crumple Full Team Sets      — up to 12 featured
 *   5. Blue Parallel Full Team Sets     — up to 12 featured
 *   6. White Full Team Sets             — up to 12 featured
 *   7. Which Version Are You Collecting?
 *   8. Missing Stickers band
 *
 * Shopify is the source of truth for all Team Set commerce data
 * (price, image, title, inventory, variant IDs). CollectorUp carries
 * NO hard-coded Team Set prices. Featured selection (which 12 per
 * variation) will resolve via a Shopify tag / metafield / collection
 * ordering; wiring is deferred until the Team Set catalog is imported.
 * Until then the sections render polished "Coming Soon" empty states.
 */
export default function Shop() {
  // Live Shopify Storefront data — one fetch per variation on mount.
  // Shopify remains the SINGLE source of truth for price, inventory,
  // images, titles, availability, and URLs. The main /shop page renders
  // the hand-curated "Featured *" manual collections (12 products in
  // merchant-controlled order). The full-catalog collections still power
  // the /shop/team-sets/{variation} View-All pages.
  const [goldFeatured,  setGoldFeatured]  = useState([]);
  const [blueFeatured,  setBlueFeatured]  = useState([]);
  const [whiteFeatured, setWhiteFeatured] = useState([]);
  const [premiumGold,   setPremiumGold]   = useState([]);
  const [premiumLoading, setPremiumLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [g, b, w, prem] = await Promise.all([
        fetchFeaturedTeamSets("gold",  8),
        fetchFeaturedTeamSets("blue",  8),
        fetchFeaturedTeamSets("white", 8),
        fetchGoldSinglesPremium(28),
      ]);
      if (cancelled) return;
      setGoldFeatured(g.products);
      setBlueFeatured(b.products);
      setWhiteFeatured(w.products);
      setPremiumGold(prem.products || []);
      setPremiumLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="shop-page">
      <SEO
        title="Store"
        description="Shop Panini collector products — albums, sticker packs, boxes, Gold Crumples, Blue Parallels, White Full Team Sets, and more. Nationwide shipping."
      />

      {/* 1. HERO (unchanged) */}
      <section className="hero-black" data-testid="shop-hero">
        <div className="container-gm relative pt-14 pb-16 md:pt-20 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <div className="flex items-center gap-3">
                  <span className="sticker sticker-red">THE STORE</span>
                  <span className="sticker sticker-white">SHIPS NATIONWIDE</span>
                </div>
                <h1 className="h-display text-white mt-6 text-5xl sm:text-6xl md:text-7xl" data-testid="shop-hero-title">
                  SHOP<br />COLLECTOR<br />
                  <span className="text-[var(--panini-yellow)]">PRODUCTS.</span>
                </h1>
                <p className="text-white/85 mt-6 max-w-xl text-base md:text-lg leading-relaxed" data-testid="shop-hero-copy">
                  Browse albums, sticker packs, boxes, Gold Crumples, Blue Parallels,
                  White Full Team Sets, and more. Nationwide shipping is available
                  throughout the United States.
                </p>
                <div className="flex flex-wrap gap-3 mt-8">
                  <a href="#featured" className="btn-yellow" data-testid="shop-cta-browse-store">
                    <ShoppingBag size={16} /> Browse the store <ArrowDown size={16} />
                  </a>
                  <Link to="/order-missing" className="btn-red" data-testid="shop-cta-order-missing">
                    <Send size={16} /> Order My Missing Stickers
                  </Link>
                </div>
                <div className="flex flex-wrap items-center gap-5 mt-10">
                  <Perk icon={<Truck size={16} />} label="Nationwide" suffix="shipping" />
                  <div className="w-px h-8 bg-white/20" />
                  <Perk icon={<Sparkles size={16} />} label="Curated" suffix="by Gil" />
                  <div className="w-px h-8 bg-white/20" />
                  <Perk icon={<SoccerBall size={14} strokeColor="#FFCB05" />} label="Since day" suffix="one" />
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div initial={{ opacity: 0, scale: 0.96, rotate: 3 }}
                          animate={{ opacity: 1, scale: 1, rotate: 3 }}
                          transition={{ duration: 0.6, delay: 0.15 }}
                          className="rounded-3xl overflow-hidden border-4 border-white shadow-[10px_10px_0_var(--panini-red)] bg-white"
                          data-testid="shop-hero-sealed-packs-art">
                <CollectionArt artKey="packs" />
              </motion.div>
            </div>
          </div>
        </div>
        <div className="field-line" aria-hidden />
      </section>

      {/* 2. CATEGORY LAUNCHER — six large tap-friendly buttons.
          Replaces the old grouped sticky slider nav. See
          `components/shop/ShopCategoryLauncher.jsx`. */}
      <ShopCategoryLauncher />

      {/* 3. FEATURED COLLECTIBLES (existing Shopify Buy Button, untouched) */}
      <section id="featured" className="container-gm py-14 md:py-20 scroll-mt-32"
               data-testid="shop-featured-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="kicker kicker-red">FEATURED COLLECTIBLES</div>
            <h2 className="h-display text-3xl md:text-5xl mt-3">
              Albums, boxes, packs,{" "}
              <span className="underline decoration-[var(--panini-yellow)] decoration-[8px] underline-offset-[6px]">
                & more.
              </span>
            </h2>
            <p className="text-[var(--smoke)] max-w-2xl mt-3">
              Albums, boxes, packs, complete sets, collectibles & more.
            </p>
          </div>
          <p className="text-[var(--smoke)] max-w-md">
            Cart, checkout, taxes, and shipping are handled securely by Shopify.
          </p>
        </div>
        <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-4 sm:p-6 md:p-8 shadow-[10px_10px_0_var(--panini-red)]"
             data-testid="shop-storefront-frame">
          <FeaturedProductGrid />
          {/* Keep the Shopify Buy Button embed mounted (hidden) so the
              shared cart drawer/checkout it initialises remains available
              to the CollectorUp-native cards below via shopifyCart.js. */}
          <div className="sr-only" aria-hidden data-testid="shopify-buy-button-hidden">
            <ShopifyBuyButton />
          </div>
        </div>
      </section>

      {/* 4. GOLD */}
      <TeamSetSection id="gold-team-sets" variation="gold"
                      eyebrow="PREMIUM TEAM SETS"
                      heading="GOLD CRUMPLE FULL TEAM SETS"
                      description="Complete 20-sticker country sets in the premium Gold Crumple variation."
                      products={goldFeatured}
                      viewAllHref="/shop/team-sets/gold"
                      viewAllLabel="VIEW ALL GOLD TEAM SETS" />

      {/* 5. BLUE */}
      <TeamSetSection id="blue-team-sets" variation="blue"
                      eyebrow="PARALLEL TEAM SETS"
                      heading="BLUE PARALLEL FULL TEAM SETS"
                      description="Complete 20-sticker country sets in the Blue Parallel variation."
                      products={blueFeatured}
                      viewAllHref="/shop/team-sets/blue"
                      viewAllLabel="VIEW ALL BLUE TEAM SETS" />

      {/* 6. WHITE */}
      <TeamSetSection id="white-team-sets" variation="white"
                      eyebrow="ORIGINAL TEAM SETS"
                      heading="WHITE FULL TEAM SETS"
                      description="Complete 20-sticker country sets from the regular White collection."
                      products={whiteFeatured}
                      viewAllHref="/shop/team-sets/white"
                      viewAllLabel="VIEW ALL WHITE TEAM SETS" />

      {/* 6b. PREMIUM GOLD SINGLES — $20+ (live Shopify) */}
      <section id="gold-singles-premium"
               className="container-gm py-14 md:py-20 scroll-mt-32"
               data-testid="shop-premium-gold-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="kicker kicker-yellow">GOLD SINGLES · PREMIUM</div>
            <h2 className="h-display text-3xl md:text-5xl mt-3"
                data-testid="shop-premium-gold-title">
              PREMIUM GOLD SINGLES —{" "}
              <span className="underline decoration-[var(--panini-yellow)] decoration-[8px] underline-offset-[6px]">
                $20+
              </span>
            </h2>
            <p className="text-[var(--smoke)] max-w-2xl mt-3">
              The highest-value Gold Crumple singles in the CollectorUp catalog —
              live Shopify prices, availability, and checkout.
            </p>
          </div>
          <Link to="/shop/gold-singles"
                className="btn-ghost self-start md:self-end"
                data-testid="shop-premium-gold-view-all-inline">
            VIEW ALL 980 GOLD SINGLES <ArrowRight size={16} />
          </Link>
        </div>
        {premiumLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
               data-testid="shop-premium-gold-loading">
            {[0,1,2,3,4,5,6,7].map(i => (
              <div key={i} className="rounded-2xl border-2 border-neutral-200 bg-neutral-100 aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : premiumGold.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-[var(--ink)]/25 bg-neutral-50 p-10 md:p-14 text-center"
               data-testid="shop-premium-gold-empty-state">
            <p className="text-lg text-[var(--smoke)] max-w-xl mx-auto">
              No Premium Gold Singles available right now — check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
               data-testid="shop-premium-gold-grid">
            {premiumGold.map((p) => (
              <GoldSingleCard key={p.shopify_product_id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* 6c. VIEW ALL 980 GOLD SINGLES — standalone CTA band */}
      <section className="container-gm pb-14 md:pb-16"
               data-testid="shop-view-all-gold-band">
        <div className="rounded-3xl border-2 border-[var(--ink)] bg-[var(--ink)] text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 shadow-[10px_10px_0_var(--panini-yellow)]">
          <div>
            <div className="kicker kicker-yellow">FULL GOLD CATALOG</div>
            <h3 className="h-display text-white text-2xl md:text-4xl mt-2"
                data-testid="shop-view-all-gold-heading">
              Browse every Gold Crumple single.
            </h3>
            <p className="text-white/80 mt-2 max-w-xl text-sm md:text-base">
              Search 980 Gold products by sticker code, player, country, or subject.
              Three price buckets — Premium $20+, $10–$19, and $9 &amp; under.
            </p>
          </div>
          <Link to="/shop/gold-singles"
                className="btn-yellow shadow-[6px_6px_0_var(--panini-red)] whitespace-nowrap self-start md:self-auto"
                data-testid="shop-view-all-gold-cta">
            VIEW ALL 980 GOLD SINGLES <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* 7. WHICH VERSION ARE YOU COLLECTING? */}
      <section className="container-gm pb-14 md:pb-20" data-testid="shop-version-picker">
        <h2 className="h-display text-2xl md:text-4xl text-center mb-8">
          Which version are you collecting?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {[
            { key: "white", to: "#white-team-sets", label: "WHITE", sub: "Original Collection", bg: "bg-white text-[var(--ink)] border-[var(--ink)]/25" },
            { key: "blue",  to: "#blue-team-sets",  label: "BLUE",  sub: "Parallel Collection", bg: "bg-[#1e4fb8] text-white border-[#1e4fb8]" },
            { key: "gold",  to: "#gold-team-sets",  label: "GOLD",  sub: "Premium Crumple Collection", bg: "bg-[#d4af37] text-[var(--ink)] border-[#d4af37]" },
          ].map((v) => (
            <a key={v.key} href={v.to}
               onClick={(e) => { e.preventDefault(); document.getElementById(v.to.slice(1))?.scrollIntoView({ behavior: "smooth" }); }}
               className={`rounded-2xl border-2 p-6 flex items-center justify-between hover:opacity-90 transition ${v.bg}`}
               data-testid={`shop-version-pick-${v.key}`}>
              <div>
                <div className="font-display text-2xl">{v.label}</div>
                <div className="text-sm opacity-80">{v.sub}</div>
              </div>
              <ArrowRight size={20} />
            </a>
          ))}
        </div>
      </section>

      {/* 8. MISSING STICKERS (workflow untouched — wrapper anchor only) */}
      <section id="missing-stickers" className="pb-16 md:pb-24 scroll-mt-32" data-testid="shop-missing-band">
        <div className="container-gm">
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white overflow-hidden shadow-[10px_10px_0_var(--ink)]">
            <div className="relative p-8 md:p-14">
              <div className="absolute -top-4 -right-4 rotate-[8deg]">
                <span className="sticker sticker-yellow">HELP DESK</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="kicker kicker-yellow">MOST-LOVED SERVICE</div>
                  <h2 className="h-display text-white text-4xl md:text-6xl mt-4 leading-[0.98]">
                    NEED HELP<br />COMPLETING<br />
                    <span className="text-[var(--panini-yellow)]">YOUR ALBUM?</span>
                  </h2>
                  <p className="mt-6 text-white/90 text-lg leading-relaxed max-w-lg">
                    <strong>FULL TEAM SETS</strong> buy an entire country&apos;s 20-sticker team.
                    <br /><strong>MISSING STICKERS</strong> order only the individual stickers needed to finish an album.
                  </p>
                  <p className="mt-4 text-white/90 text-lg leading-relaxed max-w-lg font-semibold">
                    Order your complete missing list and we&apos;ll prepare everything for you.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-8">
                    <Link to="/order-missing" className="btn-yellow shadow-[6px_6px_0_var(--ink)]" data-testid="shop-band-cta-missing">
                      <Send size={16} /> Order my missing list <ArrowRight size={16} />
                    </Link>
                    <Link to="/contact" className="btn-ghost text-white border-white/60 hover:bg-white/10" data-testid="shop-band-cta-contact">
                      Ask a question
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <div className="rounded-2xl border-4 border-[var(--panini-yellow)] bg-[var(--ink)] p-7 md:p-8 relative">
                    <div className="font-mono text-[var(--panini-yellow)] text-xs">// paste your list — anything goes</div>
                    <pre className="mt-3 font-mono text-white text-sm leading-7 whitespace-pre-wrap">
{`ARG 2 x3, 5, 17
BRA 4, 6 x2, 9
GER 12, 18, 21
FWC-26 Foil #05, #12`}
                    </pre>
                    <div className="mt-6 pt-4 border-t border-white/15 text-white/60 text-xs font-mono">
                      ↳ We verify, price, and confirm.<br />
                      ↳ You pay. Order is confirmed.<br />
                      ↳ We start preparing immediately.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Perk({ icon, label, suffix }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-full border border-white/30 bg-white/10 flex items-center justify-center text-[var(--panini-yellow)]">
        {icon}
      </span>
      <div>
        <div className="font-display text-white text-base leading-none">{label}</div>
        <div className="kicker kicker-cream text-[10px]">{suffix}</div>
      </div>
    </div>
  );
}
